/**
 * useSyncTrigger - shared cross-system sync trigger.
 *
 * Owns the "Sync latest data" interaction used by:
 *   - RefreshDataButton (inspector header icon)
 *   - useInspectorAlerts freshness alert action
 *
 * Loops over the FP's active connections (Lightspeed + Xero only - Skimmer
 * is intentionally excluded per project_skimmer_not_active), POSTs to
 * /sync/{system}/{fpCode}, tolerates 409 (already-running), and refetches
 * /sync/operations/active/{fpCode} 500ms later so the sync bar can render.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { apiClient } from '../services/api'
import { useNotify } from '../components/shared/ToastProvider'
import { debugLog, debugWarn } from '../utils/debugLog'

interface ActiveSync {
  operation_id: string
  sync_system: string
  status: string
  progress_percentage: number
  current_step: string
}

interface FranchiseDataLike {
  franchise_name?: string
  has_complete_lightspeed_connection?: boolean
  has_complete_xero_connection?: boolean
}

interface UseSyncTriggerOptions {
  fpCode: string
  franchiseData?: FranchiseDataLike | null
  onSyncStarted?: () => void
}

interface UseSyncTriggerReturn {
  triggerSync: () => Promise<void>
  loading: boolean
  activeSyncs: ActiveSync[]
  checking: boolean
}

const getSystemsToSync = (franchiseData?: FranchiseDataLike | null): string[] => {
  const systems: string[] = []
  if (franchiseData?.has_complete_lightspeed_connection) systems.push('lightspeed')
  if (franchiseData?.has_complete_xero_connection) systems.push('xero')
  return systems
}

const fetchActiveSyncs = async (fpCode: string): Promise<ActiveSync[]> => {
  const timestamp = Date.now()
  const response = await apiClient.get(`/sync/operations/active/${fpCode}?t=${timestamp}`)
  return response.data || []
}

export const useSyncTrigger = ({
  fpCode,
  franchiseData,
  onSyncStarted,
}: UseSyncTriggerOptions): UseSyncTriggerReturn => {
  const { showInfo } = useNotify()
  const [loading, setLoading] = useState(false)
  const [activeSyncs, setActiveSyncs] = useState<ActiveSync[]>([])
  const [checking, setChecking] = useState(true)
  const mountedRef = useRef(true)
  const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    mountedRef.current = false
    if (pendingTimeoutRef.current) {
      clearTimeout(pendingTimeoutRef.current)
      pendingTimeoutRef.current = null
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const check = async () => {
      try {
        const data = await fetchActiveSyncs(fpCode)
        if (!cancelled && mountedRef.current) setActiveSyncs(data)
      } catch (error) {
        console.error('Error checking active syncs:', error)
        if (!cancelled && mountedRef.current) setActiveSyncs([])
      } finally {
        if (!cancelled && mountedRef.current) setChecking(false)
      }
    }
    check()
    return () => {
      cancelled = true
    }
  }, [fpCode])

  const triggerSync = useCallback(async () => {
    const systems = getSystemsToSync(franchiseData)
    if (systems.length === 0) {
      debugWarn(`No active system connections for ${fpCode}; skipping sync.`)
      return
    }

    setLoading(true)
    const failures: { system: string; error: string }[] = []
    let anyStarted = false
    // Authoritative display name from the trigger response, so the ack
    // toast names the FP ("Poolwerx Sherwood") rather than its code.
    let resolvedName: string | undefined

    try {
      for (const system of systems) {
        try {
          const response = await apiClient.post(`/sync/${system}/${fpCode}`, {
            sync_type: 'smart',
            priority: 2,
          })
          if (!resolvedName) resolvedName = response.data?.franchise_name
          debugLog(`✅ Started ${system} sync for ${fpCode}`)
          anyStarted = true
        } catch (error: any) {
          if (error.response?.status === 409) {
            debugLog(`⚠️ ${system} sync already running for ${fpCode}`)
            // 409 means the system already has the same sync queued or
            // running for this FP - from the user's perspective it IS
            // active, so the header bar should still surface it.
            anyStarted = true
          } else {
            console.error(`❌ Failed to start ${system} sync:`, error)
            failures.push({
              system,
              error: error.response?.data?.detail || error.message,
            })
          }
        }
      }

      // Broadcast so any sibling sync-status hooks (notably the
      // InspectorLayout header bar via ``useActiveSyncStatus``) can
      // refetch immediately instead of waiting up to 30s for their
      // next idle-cadence poll. Same window-event pattern as the
      // existing ``evop:sync-completed`` signal in
      // ``useSyncFreshness.ts``.
      if (anyStarted) {
        window.dispatchEvent(
          new CustomEvent('evop:sync-triggered', { detail: { fpCode } }),
        )
        // Single source of the queued-sync ack so every caller (header
        // refresh icon + freshness alert action) behaves identically.
        // Single-state truth: this fires once when the sync is accepted, so
        // it says "queued" only - the header sync bar reports progress.
        // Prefer the FP name (response is authoritative; franchiseData covers
        // the all-409 case where no fresh response carried it) over the code.
        const displayName =
          resolvedName || franchiseData?.franchise_name || fpCode
        showInfo(`Sync queued for ${displayName}`)
      }

      if (onSyncStarted) onSyncStarted()

      if (failures.length > 0) {
        debugWarn('Some syncs failed to start:', failures)
      }
    } catch (error) {
      console.error('Error triggering refresh:', error)
    } finally {
      if (mountedRef.current) setLoading(false)
      if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current)
      pendingTimeoutRef.current = setTimeout(async () => {
        pendingTimeoutRef.current = null
        try {
          const data = await fetchActiveSyncs(fpCode)
          if (mountedRef.current) setActiveSyncs(data)
        } catch (error) {
          console.error('Error checking active syncs after trigger:', error)
        }
      }, 500)
    }
  }, [fpCode, franchiseData, onSyncStarted, showInfo])

  return { triggerSync, loading, activeSyncs, checking }
}

export default useSyncTrigger
